// ⚠️ A RELATIVE PATH WITH `.ts`, NOT THE `@/` ALIAS, ON PURPOSE. `scripts/draft-whats-new-digest.mjs` loads this file in plain Node
// to work out which entries each person is shown, so the weekly email's recipient lists follow the very rule the app uses and not a
// copy of it. Node resolves neither the alias nor a missing extension; `allowImportingTsExtensions` makes this legal for Vite and `tsc`.
// Anything else this file imports must be loadable the same way: `lib/access.ts` imports nothing, and the rest is `import type`.
import { isFieldLeaderOnly, type Viewer } from '../../../lib/access.ts';
import type { Audience, WhatsNewEntry } from '../types';

/**
 * The answers an entry's `audience.test` is allowed to ask, worked out from `/users/me`.
 * Admin holds every permission, the way `toAccess` in the Help drawer treats it.
 */
export function audienceOf(me: Viewer): Audience {
  const admin = me.roles.includes('Admin');
  const can = (permission: string) => admin || me.permissions.includes(permission);
  return {
    admin,
    fieldLeaderOnly: isFieldLeaderOnly(me),
    noRole: me.roles.length === 0,
    editsProjects: can('projects.edit'),
    viewsFinancials: can('financials.view'),
  };
}

/**
 * The entries this person should be told about, in the order given: in the What's new list, in the number on its button, and in the
 * weekly email's recipient lists, which all call this.
 *
 * ⚠️ A FIELD LEADER, AND A PERSON WITH NO ROLE, ARE SHOWN ONLY THE ENTRIES WITH NO `audience`, whatever an entry's `test` would say
 * (the product owner, 2026-10-23). Neither has the office screens most limited entries are about; without this, a change limited to
 * "everyone who is not a field leader" reached a person with no role at all. It is applied here, before `test` is asked, so an entry's
 * author cannot widen it by accident. A field leader who also holds an office grant is not `fieldLeaderOnly`, and is not narrowed.
 */
export function entriesFor(entries: readonly WhatsNewEntry[], audience: Audience): WhatsNewEntry[] {
  if (audience.fieldLeaderOnly || audience.noRole) return entries.filter((e) => !e.audience);
  return entries.filter((e) => !e.audience || e.audience.test(audience));
}
